import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import Swal from "sweetalert2";
import { brand } from "../../../config/theme";
import Modal from "../../../components/base/Modal";
import { buildThemeClasses } from "../theme";
import {
  fetchAgents,
  createAgent,
  updateAgent,
  deleteAgent,
  fetchVoices,
  fetchElevenLabsAgents,
  importElevenLabsAgent,
  type Agent,
  type AgentDraft,
  type AgentType,
  type AgentStatus,
  type AgentUseCase,
  type DataField,
  type VoiceOption,
  type ElevenLabsAgentSummary,
} from "../services/agentsService";
import { AGENT_TEMPLATES, TEMPLATE_BY_USE_CASE } from "../services/agentTemplates";
import { LEAD_PRODUCTS } from "../services/leadsService";
import { OwnerByProductCard } from "../components/OwnerByProductCard";

import { DIALOG_COLORS } from "../../../components/ui";
// ===========================================================================
// Agentes — LIVE CRUD. The people / bots that own leads and run calls. Each
// agent is either a human sales rep or an AI voice agent (ElevenLabs config).
// Console users create / edit / delete them here; the row count shows how many
// leads are currently assigned to each agent (owner).
// ===========================================================================

const POLL_MS = 30000;

const TYPE_META: Record<AgentType, { label: string; icon: string; tone: string }> = {
  human: { label: "Humano", icon: "ri-user-3-line", tone: "bg-sky-500/15 text-sky-500" },
  ai: { label: "IA", icon: "ri-robot-2-line", tone: "bg-violet-500/15 text-violet-500" },
};

const STATUS_META: Record<AgentStatus, { label: string; tone: string }> = {
  active: { label: "Activo", tone: "bg-emerald-500/15 text-emerald-500" },
  inactive: { label: "Inactivo", tone: "bg-gray-400/15 text-gray-500" },
};

const EMPTY_DRAFT: AgentDraft = {
  name: "",
  type: "human",
  email: null,
  phone: null,
  role: null,
  status: "active",
  product: null,
  elevenLabsAgentId: null,
  voiceId: null,
  prompt: null,
  useCase: "custom",
  objective: null,
  openingLine: null,
  script: null,
  questions: [],
  dataToCollect: [],
  guardrails: null,
  tone: null,
  maxDurationMin: 3,
  useKnowledgeBase: false,
};

/** kebab/snake key from a label, so dataToCollect rows get a stable id. */
function slugifyKey(raw: string): string {
  return raw
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 40);
}

type ModalState = null | { mode: "add" } | { mode: "edit"; agent: Agent };

// `filterType` splits this CRUD into two menu entries: "Humanos" (filterType
// "human") and "Agentes IA" (filterType "ai"). When set, the table shows only
// that type, new agents are locked to it, and the Tipo column is dropped.
export default function AgentesLive({ filterType }: { filterType?: AgentType } = {}) {
  const tc = buildThemeClasses();
  const isAI = filterType === "ai";
  const isHuman = filterType === "human";

  const [agents, setAgents] = useState<Agent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [modal, setModal] = useState<ModalState>(null);
  const [voices, setVoices] = useState<VoiceOption[]>([]);
  const [importing, setImporting] = useState(false);
  const firstLoad = useRef(true);

  // Load the ElevenLabs voice list once for the form's voice picker (best
  // effort — empty list just falls back to a free-text Voice ID).
  useEffect(() => {
    let alive = true;
    fetchVoices().then((v) => alive && setVoices(v));
    return () => {
      alive = false;
    };
  }, []);

  const load = useCallback(async () => {
    if (firstLoad.current) setLoading(true);
    setError(null);
    try {
      const page = await fetchAgents({ limit: 300 });
      setAgents(page.data);
    } catch (e) {
      const err = e as { response?: { status?: number; data?: { message?: string } }; message?: string };
      // 404 = endpoint not deployed yet → just show the empty state.
      if (err.response?.status === 404) setAgents([]);
      else setError(err.response?.data?.message || err.message || "No se pudieron cargar los agentes");
    } finally {
      setLoading(false);
      firstLoad.current = false;
    }
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, POLL_MS);
    return () => clearInterval(id);
  }, [load]);

  const counts = useMemo(() => {
    const human = agents.filter((a) => a.type === "human").length;
    const ai = agents.filter((a) => a.type === "ai").length;
    return { human, ai };
  }, [agents]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    let list = filterType ? agents.filter((a) => a.type === filterType) : agents;
    if (q) {
      list = list.filter((a) =>
        [a.name, a.email, a.phone, a.role].filter(Boolean).some((v) => String(v).toLowerCase().includes(q)),
      );
    }
    return list;
  }, [agents, query, filterType]);

  const handleSubmit = async (draft: AgentDraft) => {
    try {
      if (modal?.mode === "edit") {
        const updated = await updateAgent(modal.agent.id, draft);
        setAgents((prev) => prev.map((a) => (a.id === modal.agent.id ? { ...a, ...updated } : a)));
      } else {
        const created = await createAgent(draft);
        setAgents((prev) => [created, ...prev]);
      }
      setModal(null);
    } catch (e) {
      const err = e as { response?: { data?: { message?: string } }; message?: string };
      Swal.fire({
        icon: "error",
        title: "Error",
        text: err.response?.data?.message || err.message || "No se pudo guardar el agente",
        confirmButtonColor: brand.primary,
      });
    }
  };

  const removeAgent = async (agent: Agent) => {
    const res = await Swal.fire({
      icon: "warning",
      title: "Eliminar agente",
      text: `¿Eliminar a "${agent.name}"? Los leads asignados no se borran.`,
      showCancelButton: true,
      confirmButtonText: "Eliminar",
      cancelButtonText: "Cancelar",
      confirmButtonColor: DIALOG_COLORS.danger,
    });
    if (!res.isConfirmed) return;
    setAgents((prev) => prev.filter((a) => a.id !== agent.id));
    try {
      await deleteAgent(agent.id);
    } catch {
      await load();
      Swal.fire({ icon: "error", title: "Error", text: "No se pudo eliminar el agente", confirmButtonColor: brand.primary });
    }
  };

  return (
    <div className="h-full w-full overflow-y-auto p-6">
      <div className="mx-auto max-w-6xl">
        {/* Header */}
        <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary-500/15 text-primary-500">
              <i className={`${isAI ? "ri-robot-2-line" : isHuman ? "ri-user-3-line" : "ri-customer-service-2-line"} text-xl`} />
            </span>
            <div>
              <h1 className={`text-xl font-bold ${tc.text}`}>
                {isAI ? "Agentes IA" : isHuman ? "Agentes humanos" : "Agentes CRM"}
              </h1>
              <p className={`text-sm ${tc.textSecondary}`}>
                {isAI
                  ? `Agentes de voz IA para llamadas automáticas · ${counts.ai} configurados`
                  : isHuman
                    ? `Vendedores y ejecutivos que gestionan leads · ${counts.human} registrados`
                    : `Vendedores y agentes IA · ${counts.human} humanos · ${counts.ai} IA`}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <div className={`flex items-center gap-2 rounded-lg border px-3 py-2 ${tc.border} ${tc.cardBg}`}>
              <i className={`ri-search-line ${tc.textSecondary}`} />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Buscar agentes..."
                className={`bg-transparent text-sm outline-none ${tc.text}`}
              />
            </div>
            {!isHuman && (
              <button
                onClick={() => setImporting(true)}
                className="flex items-center gap-2 rounded-lg border px-4 py-2 text-sm font-medium transition-colors border-violet-500/40 text-violet-500 hover:bg-violet-500/10"
              >
                <i className="ri-download-cloud-2-line" />
                <span className="whitespace-nowrap">Importar de ElevenLabs</span>
              </button>
            )}
            <button
              onClick={() => setModal({ mode: "add" })}
              className="flex items-center gap-2 rounded-lg bg-primary-500 px-4 py-2 text-sm font-medium text-on-primary transition-colors hover:bg-primary-400"
            >
              <i className="ri-add-line" />
              <span className="whitespace-nowrap">
                {isAI ? "Nuevo agente IA" : isHuman ? "Nuevo vendedor" : "Nuevo agente"}
              </span>
            </button>
          </div>
        </div>

        {/* Responsable por producto: solo aplica a personas, no a los agentes IA. */}
        {!isAI && <OwnerByProductCard agents={agents} />}

        {/* Table */}
        <div className={`rounded-2xl border ${tc.cardBorder} ${tc.cardBg}`}>
          {loading ? (
            <div className={`p-10 text-center text-sm ${tc.textSecondary}`}>
              <i className="ri-loader-4-line mr-2 animate-spin" />
              Cargando agentes...
            </div>
          ) : error ? (
            <div className="p-10 text-center">
              <i className="ri-error-warning-line mb-2 text-3xl text-red-500" />
              <p className="text-sm text-red-500">{error}</p>
              <button onClick={load} className="mt-3 rounded-lg bg-primary-500 px-4 py-2 text-sm text-on-primary hover:bg-primary-400">
                Reintentar
              </button>
            </div>
          ) : filtered.length === 0 ? (
            <div className={`p-10 text-center text-sm ${tc.textSecondary}`}>
              <i className={`${isAI ? "ri-robot-2-line" : isHuman ? "ri-user-3-line" : "ri-customer-service-2-line"} mb-2 block text-3xl`} />
              {isAI
                ? "Aún no hay agentes IA. Crea o importa tu primer agente de voz."
                : isHuman
                  ? "Aún no hay vendedores. Crea tu primer agente humano."
                  : "Aún no hay agentes. Crea tu primer vendedor o agente IA."}
            </div>
          ) : (
            <div className="w-full overflow-x-auto p-4">
              <table className="w-full overflow-hidden rounded-xl text-xs shadow-sm">
                <thead className="bg-primary-500 text-on-primary">
                  <tr>
                    <th className="p-3 text-left font-bold">Agente</th>
                    {!filterType && <th className="p-3 text-center font-bold">Tipo</th>}
                    <th className="p-3 text-left font-bold">Rol</th>
                    <th className="p-3 text-left font-bold">Correo</th>
                    <th className="p-3 text-left font-bold">Teléfono</th>
                    <th className="p-3 text-center font-bold">Leads</th>
                    <th className="p-3 text-center font-bold">Estado</th>
                    <th className="p-3 text-center font-bold">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((a, i) => (
                    <tr
                      key={a.id}
                      className={`text-content ${i % 2 === 0 ? "bg-surface" : "bg-card"}`}
                    >
                      <td className="p-2 font-medium">
                        <div className="flex items-center gap-2">
                          <span>{a.name}</span>
                          {/* When the Tipo column is hidden, surface the 11L sync chip here. */}
                          {filterType && a.type === "ai" && <ElSyncChip agent={a} />}
                        </div>
                      </td>
                      {!filterType && (
                        <td className="p-2 text-center">
                          <div className="flex flex-col items-center gap-1">
                            <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 font-semibold ${TYPE_META[a.type].tone}`}>
                              <i className={TYPE_META[a.type].icon} />
                              {TYPE_META[a.type].label}
                            </span>
                            {a.type === "ai" && <ElSyncChip agent={a} />}
                          </div>
                        </td>
                      )}
                      <td className="p-2">{a.role ?? "—"}</td>
                      <td className="p-2">{a.email ?? "—"}</td>
                      <td className="p-2">{a.phone ?? "—"}</td>
                      <td className="p-2 text-center">
                        <span className="inline-flex min-w-[28px] items-center justify-center rounded-full bg-primary-500/15 px-2 py-0.5 font-semibold text-primary-500">
                          {a.leadsCount ?? 0}
                        </span>
                      </td>
                      <td className="p-2 text-center">
                        <span className={`inline-flex items-center rounded-full px-2 py-0.5 font-semibold ${STATUS_META[a.status].tone}`}>
                          {STATUS_META[a.status].label}
                        </span>
                      </td>
                      <td className="p-2">
                        <div className="flex items-center justify-center gap-1">
                          <button
                            title="Editar"
                            onClick={() => setModal({ mode: "edit", agent: a })}
                            className="rounded-md p-1.5 text-primary-500 hover:bg-primary-500/10"
                          >
                            <i className="ri-edit-line" />
                          </button>
                          <button
                            title="Eliminar"
                            onClick={() => removeAgent(a)}
                            className="rounded-md p-1.5 text-red-500 hover:bg-red-500/10"
                          >
                            <i className="ri-delete-bin-line" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <p className={`mt-3 text-xs ${tc.textSecondary}`}>
          <i className="ri-information-line mr-1" />
          {isAI
            ? "Los agentes IA usan su configuración de ElevenLabs para las llamadas automáticas."
            : isHuman
              ? "Los agentes humanos se asignan como dueños de leads."
              : "Los agentes humanos se asignan como dueños de leads. Los agentes IA usan su configuración de ElevenLabs para las llamadas automáticas."}
        </p>
      </div>

      {modal && (
        <Modal
          title={modal.mode === "edit" ? "Editar agente" : "Nuevo agente"}
          onClose={() => setModal(null)}
          themeClasses={tc}
          content={
            <AgentForm
              initial={modal.mode === "edit" ? modal.agent : undefined}
              lockedType={filterType}
              voices={voices}
              submitLabel={modal.mode === "edit" ? "Guardar" : "Crear"}
              onCancel={() => setModal(null)}
              onSubmit={handleSubmit}
            />
          }
        />
      )}

      {importing && (
        <Modal
          title="Importar agentes de ElevenLabs"
          onClose={() => setImporting(false)}
          themeClasses={tc}
          content={
            <ImportElevenLabsAgents
              existingElIds={new Set(agents.map((a) => a.elevenLabsAgentId).filter(Boolean) as string[])}
              onClose={() => setImporting(false)}
              onImported={(agent) => {
                setAgents((prev) => (prev.some((a) => a.id === agent.id) ? prev : [agent, ...prev]));
              }}
            />
          }
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Create / edit form. Toggling type reveals the AI-only config fields.
// ---------------------------------------------------------------------------
function AgentForm({
  initial,
  lockedType,
  voices,
  submitLabel,
  onCancel,
  onSubmit,
}: {
  initial?: Agent;
  lockedType?: AgentType;
  voices: VoiceOption[];
  submitLabel: string;
  onCancel: () => void;
  onSubmit: (draft: AgentDraft) => void | Promise<void>;
}) {
  const [draft, setDraft] = useState<AgentDraft>(() =>
    initial
      ? {
          name: initial.name,
          type: initial.type,
          email: initial.email,
          phone: initial.phone,
          role: initial.role,
          status: initial.status,
          product: initial.product ?? null,
          elevenLabsAgentId: initial.elevenLabsAgentId,
          voiceId: initial.voiceId,
          prompt: initial.prompt,
          useCase: initial.useCase ?? "custom",
          objective: initial.objective,
          openingLine: initial.openingLine,
          script: initial.script,
          questions: initial.questions ?? [],
          dataToCollect: initial.dataToCollect ?? [],
          guardrails: initial.guardrails,
          tone: initial.tone,
          maxDurationMin: initial.maxDurationMin ?? 3,
          useKnowledgeBase: initial.useKnowledgeBase ?? false,
        }
      : { ...EMPTY_DRAFT, type: lockedType ?? EMPTY_DRAFT.type },
  );
  const [submitting, setSubmitting] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);

  const set = <K extends keyof AgentDraft>(key: K, value: AgentDraft[K]) =>
    setDraft((d) => ({ ...d, [key]: value }));

  // Picking a use case seeds the empty fields from its template, so the user
  // gets a working script to tailor instead of a blank form. Non-empty fields
  // the user already wrote are preserved.
  const applyUseCase = (useCase: AgentUseCase) => {
    const tpl = TEMPLATE_BY_USE_CASE[useCase];
    setDraft((d) => ({
      ...d,
      useCase,
      objective: d.objective?.trim() ? d.objective : tpl.objective || null,
      openingLine: d.openingLine?.trim() ? d.openingLine : tpl.openingLine || null,
      script: d.script?.trim() ? d.script : tpl.script || null,
      tone: d.tone?.trim() ? d.tone : tpl.tone || null,
      guardrails: d.guardrails?.trim() ? d.guardrails : tpl.guardrails || null,
      questions: d.questions.length ? d.questions : [...tpl.questions],
      dataToCollect: d.dataToCollect.length ? d.dataToCollect : tpl.dataToCollect.map((x) => ({ ...x })),
      maxDurationMin: d.maxDurationMin && d.maxDurationMin !== 3 ? d.maxDurationMin : tpl.maxDurationMin,
    }));
  };

  // --- questions list helpers ---
  const setQuestion = (i: number, value: string) =>
    set("questions", draft.questions.map((q, idx) => (idx === i ? value : q)));
  const addQuestion = () => set("questions", [...draft.questions, ""]);
  const removeQuestion = (i: number) =>
    set("questions", draft.questions.filter((_, idx) => idx !== i));

  // --- dataToCollect list helpers ---
  const setField = (i: number, patch: Partial<DataField>) =>
    set(
      "dataToCollect",
      draft.dataToCollect.map((row, idx) =>
        idx === i
          ? { ...row, ...patch, key: patch.label !== undefined ? slugifyKey(patch.label) : row.key }
          : row,
      ),
    );
  const addField = () =>
    set("dataToCollect", [...draft.dataToCollect, { key: "", label: "", required: false }]);
  const removeField = (i: number) =>
    set("dataToCollect", draft.dataToCollect.filter((_, idx) => idx !== i));

  const inputCls = `w-full rounded-lg border px-3 py-2 text-sm outline-none border-line bg-app text-content`;
  const labelCls = `mb-1 block text-xs font-medium text-content-secondary`;

  const canSubmit = draft.name.trim().length > 0 && !submitting;

  const handle = async (e: FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      // Sanitize the call config: drop blank questions / unnamed data rows and
      // re-derive keys. The backend nulls all of this for human agents anyway.
      const cleaned: AgentDraft = {
        ...draft,
        name: draft.name.trim(),
        questions: draft.questions.map((q) => q.trim()).filter(Boolean),
        dataToCollect: draft.dataToCollect
          .map((d) => ({ ...d, label: d.label.trim(), key: slugifyKey(d.label) }))
          .filter((d) => d.label && d.key),
      };
      await onSubmit(cleaned);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handle} className="space-y-4">
      <div>
        <label className={labelCls}>Nombre *</label>
        <input
          value={draft.name}
          onChange={(e) => set("name", e.target.value)}
          placeholder="Nombre del agente"
          className={inputCls}
          autoFocus
        />
      </div>

      <div className={lockedType ? "" : "grid grid-cols-2 gap-3"}>
        {/* Type is fixed when the form is opened from a type-specific tab. */}
        {!lockedType && (
          <div>
            <label className={labelCls}>Tipo</label>
            <select
              value={draft.type}
              onChange={(e) => set("type", e.target.value as AgentType)}
              className={inputCls}
            >
              <option value="human">Humano</option>
              <option value="ai">IA</option>
            </select>
          </div>
        )}
        <div>
          <label className={labelCls}>Estado</label>
          <select
            value={draft.status}
            onChange={(e) => set("status", e.target.value as AgentStatus)}
            className={inputCls}
          >
            <option value="active">Activo</option>
            <option value="inactive">Inactivo</option>
          </select>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className={labelCls}>Rol</label>
          <input
            value={draft.role ?? ""}
            onChange={(e) => set("role", e.target.value || null)}
            placeholder="Ej. SDR, Closer, Manager"
            className={inputCls}
          />
        </div>
        <div>
          <label className={labelCls}>Teléfono</label>
          <input
            value={draft.phone ?? ""}
            onChange={(e) => set("phone", e.target.value || null)}
            placeholder="+52 ..."
            className={inputCls}
          />
        </div>
      </div>

      <div>
        <label className={labelCls}>Correo</label>
        <input
          type="email"
          value={draft.email ?? ""}
          onChange={(e) => set("email", e.target.value || null)}
          placeholder="agente@empresa.com"
          className={inputCls}
        />
      </div>

      {draft.type === "ai" && (
        <div className="space-y-4 rounded-lg border border-violet-500/30 bg-violet-500/5 p-3">
          <p className="flex items-center gap-2 text-xs font-semibold text-violet-500">
            <i className="ri-robot-2-line" /> Configuración del agente IA de llamadas
          </p>

          {/* Use case / purpose — seeds the script from a template. */}
          <div>
            <label className={labelCls}>Caso de uso</label>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
              {AGENT_TEMPLATES.map((t) => {
                const selected = (draft.useCase ?? "custom") === t.useCase;
                return (
                  <button
                    key={t.useCase}
                    type="button"
                    onClick={() => applyUseCase(t.useCase)}
                    title={t.description}
                    className={`flex flex-col items-center gap-1 rounded-lg border px-2 py-2 text-center text-[11px] font-medium transition-colors ${
                      selected
                        ? "border-violet-500 bg-violet-500/15 text-violet-500"
                        : "border-line text-content-secondary hover:bg-subtle"
                    }`}
                  >
                    <i className={`${t.icon} text-base`} />
                    {t.label}
                  </button>
                );
              })}
            </div>
            <p className="mt-1 text-[11px] text-content-muted">
              Elegir un caso de uso rellena el guion sugerido. Puedes editar todo.
            </p>
          </div>

          {/* Product the agent promotes — tags leads captured on cold calls. */}
          <div>
            <label className={labelCls}>Producto que promociona</label>
            <select
              value={draft.product ?? ""}
              onChange={(e) => set("product", e.target.value || null)}
              className={inputCls}
            >
              <option value="">— Sin producto específico —</option>
              {LEAD_PRODUCTS.map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </select>
            <p className="mt-1 text-[11px] text-content-muted">
              Al llamar a empresas, el lead capturado se etiqueta con este producto.
            </p>
          </div>

          <div>
            <label className={labelCls}>Objetivo de la llamada</label>
            <textarea
              value={draft.objective ?? ""}
              onChange={(e) => set("objective", e.target.value || null)}
              placeholder="¿Para qué llama este agente? Ej. confirmar datos y calificar interés."
              rows={2}
              className={inputCls}
            />
          </div>

          <div>
            <label className={labelCls}>Frase de apertura</label>
            <textarea
              value={draft.openingLine ?? ""}
              onChange={(e) => set("openingLine", e.target.value || null)}
              placeholder="Lo primero que dirá al contestar..."
              rows={2}
              className={inputCls}
            />
          </div>

          <div>
            <label className={labelCls}>Guion / puntos a cubrir</label>
            <textarea
              value={draft.script ?? ""}
              onChange={(e) => set("script", e.target.value || null)}
              placeholder="Cómo debe llevar la conversación, qué ofrecer, cómo cerrar..."
              rows={4}
              className={inputCls}
            />
          </div>

          {/* Questions to ask */}
          <div>
            <label className={labelCls}>Preguntas que debe hacer</label>
            <div className="space-y-2">
              {draft.questions.map((q, i) => (
                <div key={i} className="flex items-center gap-2">
                  <span className="text-xs text-content-muted">{i + 1}.</span>
                  <input
                    value={q}
                    onChange={(e) => setQuestion(i, e.target.value)}
                    placeholder="Escribe una pregunta..."
                    className={inputCls}
                  />
                  <button
                    type="button"
                    onClick={() => removeQuestion(i)}
                    title="Quitar"
                    className="rounded-md p-1.5 text-red-500 hover:bg-red-500/10"
                  >
                    <i className="ri-close-line" />
                  </button>
                </div>
              ))}
            </div>
            <button
              type="button"
              onClick={addQuestion}
              className="mt-2 flex items-center gap-1 text-xs font-medium text-violet-500 hover:text-violet-400"
            >
              <i className="ri-add-line" /> Agregar pregunta
            </button>
          </div>

          {/* Data to collect */}
          <div>
            <label className={labelCls}>Información a recopilar</label>
            <div className="space-y-2">
              {draft.dataToCollect.map((row, i) => (
                <div key={i} className="flex items-center gap-2">
                  <input
                    value={row.label}
                    onChange={(e) => setField(i, { label: e.target.value })}
                    placeholder="Ej. Fecha de pago"
                    className={inputCls}
                  />
                  <label
                    className="flex shrink-0 items-center gap-1 text-[11px] text-content-muted"
                    title="Obligatorio"
                  >
                    <input
                      type="checkbox"
                      checked={row.required}
                      onChange={(e) => setField(i, { required: e.target.checked })}
                    />
                    Oblig.
                  </label>
                  <button
                    type="button"
                    onClick={() => removeField(i)}
                    title="Quitar"
                    className="rounded-md p-1.5 text-red-500 hover:bg-red-500/10"
                  >
                    <i className="ri-close-line" />
                  </button>
                </div>
              ))}
            </div>
            <button
              type="button"
              onClick={addField}
              className="mt-2 flex items-center gap-1 text-xs font-medium text-violet-500 hover:text-violet-400"
            >
              <i className="ri-add-line" /> Agregar dato
            </button>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Tono</label>
              <input
                value={draft.tone ?? ""}
                onChange={(e) => set("tone", e.target.value || null)}
                placeholder="Ej. cálido y profesional"
                className={inputCls}
              />
            </div>
            <div>
              <label className={labelCls}>Duración máx. (min)</label>
              <input
                type="number"
                min={1}
                max={30}
                value={draft.maxDurationMin ?? 3}
                onChange={(e) => set("maxDurationMin", Number(e.target.value) || 3)}
                className={inputCls}
              />
            </div>
          </div>

          <div>
            <label className={labelCls}>Restricciones / reglas</label>
            <textarea
              value={draft.guardrails ?? ""}
              onChange={(e) => set("guardrails", e.target.value || null)}
              placeholder="Qué nunca debe hacer (ej. no prometer precios, no presionar)..."
              rows={2}
              className={inputCls}
            />
          </div>

          {/* Knowledge base — ground the agent in the wiki/landing facts (#220 B). */}
          <label className="flex cursor-pointer items-start gap-2">
            <input
              type="checkbox"
              checked={draft.useKnowledgeBase ?? false}
              onChange={(e) => set("useKnowledgeBase", e.target.checked)}
              className="mt-0.5 h-4 w-4 accent-primary-500"
            />
            <span>
              <span className={labelCls}>Usar base de conocimiento</span>
              <span className="block text-[11px] text-gray-500">
                Inyecta los datos de la empresa y los productos (wiki + landing) para que el agente
                presente la información correcta y no invente. Recomendado para agentes de presentación
                / ventas salientes.
              </span>
            </span>
          </label>

          {/* Voice — picker when ElevenLabs voices are available, else free text. */}
          <div>
            <label className={labelCls}>Voz</label>
            {voices.length > 0 ? (
              <select
                value={draft.voiceId ?? ""}
                onChange={(e) => set("voiceId", e.target.value || null)}
                className={inputCls}
              >
                <option value="">— Voz por defecto —</option>
                {voices.map((v) => (
                  <option key={v.voiceId} value={v.voiceId}>
                    {v.name}
                    {v.category ? ` · ${v.category}` : ""}
                  </option>
                ))}
              </select>
            ) : (
              <input
                value={draft.voiceId ?? ""}
                onChange={(e) => set("voiceId", e.target.value || null)}
                placeholder="voice_... (ID de voz de ElevenLabs)"
                className={inputCls}
              />
            )}
          </div>

          {/* Sync error surfaced here so the user can fix + re-save. */}
          {initial?.elevenLabsSync?.status === "error" && (
            <p className="flex items-start gap-2 rounded-lg bg-red-500/10 px-3 py-2 text-[11px] text-red-500">
              <i className="ri-error-warning-line mt-0.5" />
              <span>
                No se pudo sincronizar con ElevenLabs: {initial.elevenLabsSync.error}. Guarda de
                nuevo para reintentar.
              </span>
            </p>
          )}

          {/* Advanced: managed agent id (read-only) + extra instructions */}
          <div className="border-t border-violet-500/20 pt-2">
            <button
              type="button"
              onClick={() => setShowAdvanced((v) => !v)}
              className="flex items-center gap-1 text-xs font-medium text-violet-500 hover:text-violet-400"
            >
              <i className={showAdvanced ? "ri-arrow-down-s-line" : "ri-arrow-right-s-line"} />
              Avanzado (instrucciones extra)
            </button>
            {showAdvanced && (
              <div className="mt-3 space-y-3">
                <div>
                  <label className={labelCls}>ElevenLabs Agent ID</label>
                  <input
                    value={initial?.elevenLabsAgentId ?? ""}
                    readOnly
                    placeholder="Se genera automáticamente al guardar"
                    className={`${inputCls} cursor-not-allowed opacity-70`}
                  />
                  <p className="mt-1 text-[11px] text-content-muted">
                    El agente de ElevenLabs se crea y mantiene automáticamente desde aquí.
                  </p>
                </div>
                <div>
                  <label className={labelCls}>Instrucciones adicionales</label>
                  <textarea
                    value={draft.prompt ?? ""}
                    onChange={(e) => set("prompt", e.target.value || null)}
                    placeholder="Notas o instrucciones extra que se añaden al guion generado."
                    rows={3}
                    className={inputCls}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      <div className="flex justify-end gap-2 pt-2">
        <button
          type="button"
          onClick={onCancel}
          className="rounded-lg border px-4 py-2 text-sm font-medium border-line text-content-secondary hover:bg-subtle"
        >
          Cancelar
        </button>
        <button
          type="submit"
          disabled={!canSubmit}
          className="flex items-center gap-2 rounded-lg bg-primary-500 px-4 py-2 text-sm font-medium text-on-primary transition-colors hover:bg-primary-400 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {submitting && <i className="ri-loader-4-line animate-spin" />}
          {submitLabel}
        </button>
      </div>
    </form>
  );
}

// ---------------------------------------------------------------------------
// Small ElevenLabs sync status chip shown under the "IA" badge in the table.
// ---------------------------------------------------------------------------
function ElSyncChip({ agent }: { agent: Agent }) {
  const sync = agent.elevenLabsSync;
  if (sync?.status === "ok" || (agent.elevenLabsManaged && agent.elevenLabsAgentId && !sync)) {
    return (
      <span
        className="inline-flex items-center gap-1 rounded-full bg-emerald-500/15 px-2 py-0.5 text-[10px] font-semibold text-emerald-500"
        title={`ElevenLabs: ${agent.elevenLabsAgentId ?? "sincronizado"}`}
      >
        <i className="ri-checkbox-circle-line" /> 11L
      </span>
    );
  }
  if (sync?.status === "error") {
    return (
      <span
        className="inline-flex items-center gap-1 rounded-full bg-red-500/15 px-2 py-0.5 text-[10px] font-semibold text-red-500"
        title={sync.error ?? "Error de sincronización con ElevenLabs"}
      >
        <i className="ri-error-warning-line" /> 11L
      </span>
    );
  }
  return (
    <span
      className="inline-flex items-center gap-1 rounded-full bg-gray-400/15 px-2 py-0.5 text-[10px] font-semibold text-gray-500"
      title="Sin agente de ElevenLabs"
    >
      <i className="ri-time-line" /> 11L
    </span>
  );
}

// ---------------------------------------------------------------------------
// "Importar de ElevenLabs" — lists the ConvAI agents that exist in the
// ElevenLabs workspace and lets the user pull ones not yet in the CRM into
// Agentes, so their voice/tone can be edited here.
// ---------------------------------------------------------------------------
function ImportElevenLabsAgents({
  existingElIds,
  onClose,
  onImported,
}: {
  existingElIds: Set<string>;
  onClose: () => void;
  onImported: (agent: Agent) => void;
}) {
  const [list, setList] = useState<ElevenLabsAgentSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [done, setDone] = useState<Set<string>>(new Set());

  useEffect(() => {
    let alive = true;
    fetchElevenLabsAgents()
      .then((d) => alive && setList(d))
      .catch((e) => {
        const err = e as { response?: { data?: { message?: string } }; message?: string };
        if (alive) setError(err.response?.data?.message || err.message || "No se pudieron cargar los agentes de ElevenLabs");
      });
    return () => {
      alive = false;
    };
  }, []);

  const isImported = (a: ElevenLabsAgentSummary) =>
    a.imported || existingElIds.has(a.agentId) || done.has(a.agentId);

  const doImport = async (a: ElevenLabsAgentSummary) => {
    setBusyId(a.agentId);
    try {
      const agent = await importElevenLabsAgent(a.agentId);
      setDone((prev) => new Set(prev).add(a.agentId));
      onImported(agent);
    } catch (e) {
      const err = e as { response?: { data?: { message?: string } }; message?: string };
      Swal.fire({
        icon: "error",
        title: "No se pudo importar",
        text: err.response?.data?.message || err.message || "Error al importar el agente",
        confirmButtonColor: brand.primary,
      });
    } finally {
      setBusyId(null);
    }
  };

  const rowBorder = "border-line";
  const subText = "text-content-muted";

  return (
    <div className="space-y-3">
      <p className={`text-xs ${subText}`}>
        Agentes que ya existen en tu cuenta de ElevenLabs. Impórtalos para gestionarlos aquí
        (voz, tono, guion). Los que ya están en el CRM aparecen como importados.
      </p>

      {error ? (
        <p className="rounded-lg bg-red-500/10 px-3 py-2 text-sm text-red-500">
          <i className="ri-error-warning-line mr-1" />
          {error}
        </p>
      ) : list === null ? (
        <p className={`py-6 text-center text-sm ${subText}`}>
          <i className="ri-loader-4-line mr-2 animate-spin" />
          Cargando agentes de ElevenLabs...
        </p>
      ) : list.length === 0 ? (
        <p className={`py-6 text-center text-sm ${subText}`}>
          No se encontraron agentes en ElevenLabs (o la integración no está configurada).
        </p>
      ) : (
        <div className="max-h-80 space-y-2 overflow-y-auto">
          {list.map((a) => {
            const imported = isImported(a);
            return (
              <div key={a.agentId} className={`flex items-center justify-between gap-2 rounded-lg border px-3 py-2 ${rowBorder}`}>
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium text-content">{a.name}</p>
                  <p className={`truncate text-[11px] ${subText}`}>{a.agentId}</p>
                </div>
                {imported ? (
                  <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-emerald-500/15 px-2 py-1 text-[11px] font-semibold text-emerald-500">
                    <i className="ri-check-line" /> Importado
                  </span>
                ) : (
                  <button
                    onClick={() => doImport(a)}
                    disabled={busyId === a.agentId}
                    className="inline-flex shrink-0 items-center gap-1 rounded-lg bg-violet-500 px-3 py-1.5 text-xs font-medium text-white hover:bg-violet-400 disabled:opacity-60"
                  >
                    {busyId === a.agentId ? <i className="ri-loader-4-line animate-spin" /> : <i className="ri-download-2-line" />}
                    Importar
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      <div className="flex justify-end pt-1">
        <button
          onClick={onClose}
          className="rounded-lg border px-4 py-2 text-sm font-medium border-line text-content-secondary hover:bg-subtle"
        >
          Cerrar
        </button>
      </div>
    </div>
  );
}
